// @ts-strict-ignore
import {
  Badge,
  Box,
  Button,
  Icon,
  IconName,
  Separator,
  Text,
  Tooltip,
  ValuePair,
} from '@usealloy/component-library';
import React from 'react';
import type { Document } from '~/entities/types';
import { DocumentRenderer } from './DocumentRenderer';
import { getVerifiedBadgeProps } from '../helpers/getVerifiedBadgeProps';
import type { SuppliedDataValue } from '../DocumentReviewModal';
import styled from 'styled-components';

// ============================================================
//     TYPES
// ============================================================
interface Props {
  document: Document;
  suppliedDataValues?: SuppliedDataValue[];
}

// ============================================================
//     STYLES
// ============================================================
const ScrollableBox = styled.div`
  max-height: 400px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--cl-space-4);
  width: 100%;
`;

// ============================================================
//     COMPONENT
// ============================================================

export const DocReviewBody: React.FC<Props> = ({
  document,
  suppliedDataValues,
}) => {
  const hasSuppliedData = suppliedDataValues?.length > 0;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: hasSuppliedData ? 'minmax(min-content, 480px) minmax(400px, 1fr)' : '100%',
        gap: 'var(--cl-space-2)',
        width: '100%',
        flexGrow: 1,
      }}
    >
      {/* LEFT COLUMN */}
      {hasSuppliedData && (
        <Box direction="column" justifyContent="flex-start" style={{ padding: 'var(--cl-space-2)' }}>
          {/* DOC SUPPLIED DATA BOX */}
          <Box direction="column" gap={4}>
            <Box justifyContent="space-between">
              <Text weight={700}>
                Supplied Data
              </Text>
              <Text weight={700}>
                Verified
              </Text>
            </Box>
            <Separator />

            <ScrollableBox>
              {suppliedDataValues.map(({ label, value, verified }) => {
                const { iconName, status, text } = getVerifiedBadgeProps(verified);

                return (
                  <Box key={`${label}-${value}`} justifyContent="space-between" gap={2}>
                    <ValuePair
                      isEditable={false}
                      dataType="string"
                      label={label}
                      value={value}
                    />
                    <Box alignItems="flex-end" justifyContent="flex-end">
                      <Badge
                        icon={iconName}
                        iconSize="small"
                        status={status}
                        style={{ borderRadius: 99, opacity: status === 'neutral' ? '.5' : undefined }}
                      >
                        {text}
                      </Badge>
                    </Box>
                  </Box>
                );
              })}
            </ScrollableBox>
          </Box>
        </Box>
      )}
      {/* RIGHT COLUMN */}
      <Box direction="column" style={{ padding: 'var(--cl-space-2)', backgroundColor: 'var(--cl-gray-100)' }}>
        <Box direction="column" style={{ flexGrow: 1, position: 'relative' }}>
          <DocumentRenderer document={document} />
          {document.content_type?.includes('image/') ? (
            <Tooltip content="Download" side="top">
              <Button
                style={{
                  position: 'absolute',
                  bottom: 'var(--cl-space-4)',
                  right: 'var(--cl-space-4)',
                  padding: 'var(--cl-space-2)',
                }}
              >
                <a
                  href={document.url}
                  target="_blank"
                  className="secondary inline"
                  download={`${document.name}.${document.extension}`}
                  rel="noreferrer"
                >
                  <Icon icon={IconName.Download} />
                </a>
              </Button>
            </Tooltip>
          ) : null}
        </Box>
      </Box>
    </div>
  );
};
